import React, { useEffect, useState } from 'react';
import {
  ApartmentOutlined,
  AuditOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Closing, EstimateLink } from '../../components/marketing/Marketing';
import './AboutPage.css';

const proofSteps = [
  { number: '01', title: 'Объект', text: 'Адрес, назначение, площадь и кадастровая запись собираются в одну рабочую карточку', label: 'Карточка объекта', reference: '78:31:0001234:68', status: 'данные собраны', valueLabel: 'Площадь объекта', value: '1 850 м²', note: 'бизнес-центр', rows: [['Адрес', 'Невский проспект, 68'], ['Назначение', 'деловое управление'], ['Реестр', 'характеристики объекта']] },
  { number: '02', title: 'Проверка', text: 'Спорные и неполные сведения остаются видимыми до расчёта', label: 'Контроль сведений', reference: '3 источника', status: 'проверено', valueLabel: 'Подтверждённые поля', value: '18 из 18', note: 'противоречий нет', rows: [['ЕГРН', 'площадь подтверждена'], ['Адрес', 'нормализован'], ['Назначение', 'уточнено']] },
  { number: '03', title: 'Рынок', text: 'В выборку попадают предложения, сопоставимые по функции, классу и окружению', label: 'Рыночная выборка', reference: 'Центральный район', status: 'выборка готова', valueLabel: 'Сопоставимые объекты', value: '6 сопоставимых предложений', note: 'радиус 1,8 км', rows: [['Диапазон', '148–171 тыс. ₽ / м²'], ['Медиана', '159 тыс. ₽ / м²'], ['Корректировка', 'класс и площадь']] },
  { number: '04', title: 'Основания', text: 'Итог можно разложить на параметры, источники и применённые допущения', label: 'Заключение', reference: 'Расчёт № 0024', status: 'основания собраны', valueLabel: 'Предварительная стоимость', value: '294 000 000 ₽', note: '159 000 ₽ / м²', rows: [['Метод', 'рыночное сравнение'], ['Источники', 'реестр и предложения'], ['Результат', 'экран и PDF']] },
];

const principles = [
  { icon: <FileSearchOutlined />, title: 'Источник рядом с цифрой', text: 'Пользователь видит, откуда взялось значение и на каком этапе оно было подтверждено' },
  { icon: <AuditOutlined />, title: 'Проверка до расчёта', text: 'Модель не прячет пробелы в данных, а превращает их в понятный список действий' },
  { icon: <ApartmentOutlined />, title: 'Контекст конкретного объекта', text: 'Район, класс, назначение и площадь важнее усреднённого показателя по городу' },
  { icon: <SafetyCertificateOutlined />, title: 'Честные границы результата', text: 'Предварительное заключение помогает принять следующий шаг, но не выдаётся за полный отчёт оценщика' },
];

export default function AboutPage() {
  const [activeProof, setActiveProof] = useState(0);
  const proof = proofSteps[activeProof];

  useEffect(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return undefined;
    const timer = window.setInterval(() => setActiveProof((current) => (current + 1) % proofSteps.length), 4200);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <main className="zema-public about-experience">
      <section className="about-hero">
        <img src="/images/about-studio.png" alt="Специалисты изучают планы коммерческого объекта и архитектурный макет" width="1672" height="941" decoding="async" fetchpriority="high" />
        <div className="zema-wrap about-hero__inner">
          <div className="about-hero__card">
            <span className="about-kicker">О платформе ЗЕМА</span>
            <h1>Оценка начинается не с цифры</h1>
            <p>Она начинается с проверяемых данных, понятной выборки и честного объяснения того, как получен результат.</p>
            <EstimateLink>Проверить объект</EstimateLink>
          </div>
        </div>
      </section>

      <section className="about-proof zema-wrap" aria-labelledby="about-proof-title">
        <div className="about-section-head">
          <h2 id="about-proof-title">У результата должен быть маршрут</h2>
          <p>ЗЕМА связывает разрозненные сведения в последовательный процесс, который можно проверить на каждом этапе.</p>
        </div>
        <div className="about-proof__rail">
          {proofSteps.map((step, index) => <button type="button" key={step.number} aria-pressed={activeProof === index} onClick={() => setActiveProof(index)}><span>{step.number}</span><h3>{step.title}</h3><p>{step.text}</p></button>)}
        </div>
      </section>

      <section className="about-studio zema-wrap">
        <div className="about-ledger" aria-label="Структура данных объекта" aria-live="polite">
          <div className="about-ledger__head"><span>{proof.label}</span><strong>{proof.reference}</strong><i>{proof.status}</i></div>
          <div className="about-ledger__value"><span>{proof.valueLabel}</span><strong>{proof.value}</strong><small>{proof.note}</small></div>
          <div className="about-ledger__rows">{proof.rows.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
          <div className="about-ledger__foot"><span>Этап маршрута</span><strong>{proof.number} / 04 · {proof.title}</strong></div>
        </div>
        <div className="about-studio__copy">
          <span className="about-kicker">Не чёрный ящик</span>
          <h2>Система показывает не только ответ</h2>
          <p>Задача платформы — сократить путь от адреса до обоснованного предварительного решения, не скрывая спорные места за красивой итоговой суммой.</p>
          <dl>
            <div><dt>Дата модели</dt><dd>01.01.2025</dd></div>
            <div><dt>Фокус</dt><dd>Бизнес-центры Санкт-Петербурга</dd></div>
            <div><dt>Результат</dt><dd>Экран и PDF с основаниями</dd></div>
          </dl>
        </div>
      </section>

      <section className="about-principles">
        <div className="zema-wrap">
          <div className="about-section-head about-section-head--light">
            <span className="about-kicker">Что для нас важно</span>
            <h2>Доверие строится на деталях</h2>
          </div>
          <div className="about-principles__grid">
            {principles.map((item) => <article key={item.title}><span>{item.icon}</span><h3>{item.title}</h3><p>{item.text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="about-boundary zema-wrap">
        <div><span className="about-kicker">Граница ответственности</span><h2>Предварительная оценка — основа для следующего решения</h2></div>
        <div><p>Она помогает проверить порядок величины, увидеть рынок рядом и подготовиться к переговорам, сделке или полноценной оценке.</p><p>Она не заменяет отчёт профессионального оценщика и не обещает автоматического изменения кадастровой стоимости.</p></div>
      </section>

      <Closing title="Начните с объекта, а не с догадки" />
    </main>
  );
}
